package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.AccessLevel
import com.evoapps.evoshift.domain.model.AccessRoles
import com.evoapps.evoshift.domain.model.BuiltInRole
import com.evoapps.evoshift.domain.model.Feature
import com.evoapps.evoshift.domain.model.RoleChoice
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.model.featureLevels
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.access.GetRolesUseCase
import com.evoapps.evoshift.domain.usecase.access.SetEngagementAccessUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.user.GetEmployeeDetailsUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.access_blocked_group
import evoshift.composeapp.generated.resources.access_blocked_owner
import evoshift.composeapp.generated.resources.access_blocked_self
import evoshift.composeapp.generated.resources.access_saved
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/**
 * SH-270: a person's access at a workplace ("Uprawnienia"): their role and the modifications of
 * single features. [current] is what they have now; [mine] the viewer's own levels (nobody gives
 * more than they have, functions/roles.js decides); [blocked] why it cannot be changed here.
 */
data class PersonAccessUiState(
    val isLoading: Boolean = true,
    val personName: String = "",
    val choices: List<RoleChoice> = emptyList(),
    val roleId: String = BuiltInRole.EMPLOYEE.id,
    val overrides: Map<Feature, AccessLevel> = emptyMap(),
    val current: Map<Feature, AccessLevel> = emptyMap(),
    val mine: Map<Feature, AccessLevel> = emptyMap(),
    val viewerIsOwner: Boolean = false,
    val blocked: UiText? = null,
    val isSaving: Boolean = false,
    val error: UiText? = null,
    val successMessage: UiText? = null
) {
    val role: RoleChoice? get() = choices.firstOrNull { it.id == roleId }
    val roleLevels: Map<Feature, AccessLevel> get() = role?.levels.orEmpty()
    /** Every feature's level as it will be saved. */
    val levels: Map<Feature, AccessLevel> get() = AccessRoles.effective(roleLevels, overrides)
    /** The features changed from the role. */
    val modified: Set<Feature> get() = AccessRoles.meaningful(roleLevels, overrides).keys
    /** The features the viewer may not set to [levels] (above their own). */
    val aboveOwn: Set<Feature> get() = AccessRoles.aboveOwn(levels, current, mine, viewerIsOwner)
    /** The permission older apps will show. */
    val permission: UserPermission get() = AccessRoles.floorPermission(levels)
    val canEdit: Boolean get() = blocked == null && !isLoading
    val canSave: Boolean get() = canEdit && !isSaving && aboveOwn.isEmpty()

    /** Whether the viewer may set [feature] to [level]. */
    fun allows(feature: Feature, level: AccessLevel): Boolean =
        viewerIsOwner || level.value <= (mine[feature] ?: AccessLevel.NONE).value
}

sealed interface PersonAccessEvent {
    data class SelectRole(val roleId: String) : PersonAccessEvent
    data class SetLevel(val feature: Feature, val level: AccessLevel) : PersonAccessEvent
    data class ResetFeature(val feature: Feature) : PersonAccessEvent
    /** "Przywróć uprawnienia roli": every modification goes. */
    data object ResetToRole : PersonAccessEvent
    data object Save : PersonAccessEvent
    data object MessageShown : PersonAccessEvent
}

class PersonAccessViewModel(
    private val userId: String,
    private val workplaceId: String,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val getWorkplace: GetWorkplaceUseCase,
    private val getEmployee: GetEmployeeDetailsUseCase,
    private val getRoles: GetRolesUseCase,
    private val setAccess: SetEngagementAccessUseCase
) : ViewModel() {

    private val _uiState = MutableStateFlow(PersonAccessUiState())
    val uiState: StateFlow<PersonAccessUiState> = _uiState.asStateFlow()

    init {
        load()
    }

    private fun load() {
        viewModelScope.launch {
            try {
                val me = getCurrentUser().first() ?: return@launch
                val workplace = getWorkplace(workplaceId).getOrThrow()
                val person = getEmployee(userId, includeSensitive = false).getOrThrow().user
                val engagement = person.engagements[workplaceId]
                // Managers may not read the business's roles in older rules: then built-in ones only.
                val custom = getRoles(workplace.ownerId).getOrDefault(emptyList())
                val choices = AccessRoles.choices(custom)
                val access = engagement?.access
                val roleId = access?.roleId?.takeIf { id -> choices.any { it.id == id } }
                    ?: BuiltInRole.of(engagement?.permission ?: UserPermission.EMPLOYEE).id
                val blocked = when {
                    engagement == null -> null
                    engagement.permission == UserPermission.OWNER || userId == workplace.ownerId -> UiText.Resource(Res.string.access_blocked_owner)
                    engagement.isViaGroup -> UiText.Resource(Res.string.access_blocked_group)
                    userId == me.uid -> UiText.Resource(Res.string.access_blocked_self)
                    else -> null
                }
                val viewerIsOwner = workplace.ownerId == me.uid
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        personName = person.displayName,
                        choices = choices,
                        roleId = roleId,
                        overrides = access?.overrides.orEmpty(),
                        current = engagement?.featureLevels.orEmpty(),
                        mine = me.engagements[workplaceId]?.featureLevels.orEmpty(),
                        viewerIsOwner = viewerIsOwner,
                        blocked = blocked
                    )
                }
            } catch (e: Exception) {
                if (e is kotlinx.coroutines.CancellationException) throw e
                _uiState.update { it.copy(isLoading = false, error = e.toUiText()) }
            }
        }
    }

    fun onEvent(event: PersonAccessEvent) {
        when (event) {
            is PersonAccessEvent.SelectRole -> _uiState.update { state ->
                // The modifications stay where they still differ from the new role.
                val role = state.choices.firstOrNull { it.id == event.roleId } ?: return@update state
                state.copy(roleId = role.id, overrides = AccessRoles.meaningful(role.levels, state.overrides))
            }
            is PersonAccessEvent.SetLevel -> _uiState.update { state ->
                val next = state.overrides + (event.feature to event.level)
                state.copy(overrides = AccessRoles.meaningful(state.roleLevels, next))
            }
            is PersonAccessEvent.ResetFeature -> _uiState.update { it.copy(overrides = it.overrides - event.feature) }
            PersonAccessEvent.ResetToRole -> _uiState.update { it.copy(overrides = emptyMap()) }
            PersonAccessEvent.Save -> save()
            PersonAccessEvent.MessageShown -> _uiState.update { it.copy(error = null, successMessage = null) }
        }
    }

    private fun save() {
        val state = _uiState.value
        if (!state.canSave) return
        _uiState.update { it.copy(isSaving = true) }
        viewModelScope.launch {
            setAccess(userId, workplaceId, state.roleId, state.roleLevels, state.overrides)
                .onSuccess {
                    _uiState.update {
                        it.copy(isSaving = false, current = state.levels, successMessage = UiText.Resource(Res.string.access_saved))
                    }
                }
                .onFailure { e -> _uiState.update { it.copy(isSaving = false, error = e.toUiText()) } }
        }
    }
}
